// Login / Register (legacy window/auth.js): both forms are in the DOM, one is shown. Submit closes the
// window at once and sends the request; success opens the Dashboard, failure reopens this window
// 1 s later in error mode (red description). The dead Google button is gone; the Facebook button
// only ever worked on https and is left out until the social login is revisited.
import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { cleanName, login, register } from '../account/player';
import { t } from '../i18n';
import { uiStore, type WindowInstance } from '../store/uiStore';
import { WindowFrame } from './WindowFrame';

type Form = 'login' | 'register';

export function AuthWindow({ instance }: { instance: WindowInstance }) {
  const initial = (instance.props.form as Form | undefined) ?? 'login';
  const errorMode = instance.props.error === true;
  const [form, setForm] = useState<Form>(initial);
  const [error, setError] = useState(errorMode);
  const [prevProps, setPrevProps] = useState({ initial, errorMode });
  if (initial !== prevProps.initial || errorMode !== prevProps.errorMode) {
    // Re-opened with another form (error mode): follow the new props.
    setPrevProps({ initial, errorMode });
    setForm(initial);
    setError(errorMode);
  }
  // Each form keeps its own fields (legacy LOGIN-005); switching forms clears the error text.
  const [names, setNames] = useState<Record<Form, string>>({ login: '', register: '' });
  const [passwords, setPasswords] = useState<Record<Form, string>>({ login: '', register: '' });
  // Filter while typing and keep the caret where it was (a dropped character must not throw the
  // caret to the end of the field, nor one place to the right as legacy did).
  const onNameInput = (f: Form, el: HTMLInputElement) => {
    const raw = el.value;
    const caret = el.selectionStart ?? raw.length;
    const cleaned = cleanName(raw);
    const newCaret = cleanName(raw.slice(0, caret)).length;
    if (cleaned === raw) {
      setNames({ ...names, [f]: cleaned });
      return;
    }
    // Commit the filtered value now and put the caret back before the next keystroke can land.
    flushSync(() => setNames({ ...names, [f]: cleaned }));
    el.setSelectionRange(newCaret, newCaret);
  };
  const switchTo = (f: Form) => {
    setForm(f);
    setError(false);
  };
  const nameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    nameRef.current?.focus();
  }, [form]);

  const submit = (e: { preventDefault(): void }) => {
    e.preventDefault();
    const which = form;
    const n = cleanName(names[which]);
    const p = passwords[which];
    uiStore().closeWindow('auth');
    void (which === 'login' ? login(n, p) : register(n, p)).then((result) => {
      if (result === 'ok') uiStore().openWindow('auth_dashboard');
      else setTimeout(() => uiStore().openWindow('auth', { form: which, error: true }), 1000);
    });
  };

  const renderForm = (f: Form) => (
    <div className={`auth_${f} lbx_cont_row`} style={{ display: form === f ? 'block' : 'none' }}>
      <form action="#" data-form={f} onSubmit={submit}>
        <div className="rows">
          <div className="row">
            <p className={`${f}_desc${error && form === f ? ' form_error' : ''}`} dangerouslySetInnerHTML={{ __html: error && form === f ? t(`auth.${f}-error`) : t(`auth.${f}-desc`) }} />
          </div>
          <div className="row">
            <span>{t('auth.label-player-name')}</span>
            <input ref={form === f ? nameRef : undefined} type="text" name="name" value={names[f]} placeholder={t('auth.placeholder-player-name')} maxLength={20} required pattern=".{3,20}" onChange={(e) => onNameInput(f, e.target)} />
          </div>
          <div className="row">
            <span>{t('auth.label-password')}</span>
            <input type="password" name="password" value={passwords[f]} placeholder={t('auth.placeholder-password')} maxLength={20} required pattern=".{3,20}" onChange={(e) => setPasswords({ ...passwords, [f]: e.target.value })} />
          </div>
          <div className="auth_valid_info">{t('auth.valid-info')}</div>
        </div>
        <div className={`auth_${f} lbx_buttons`}>
          <input type="submit" value={t(`auth.${f}-button`)} />
          <span className="or"> {t('or')} </span>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              switchTo(f === 'login' ? 'register' : 'login');
            }}
          >
            {t(f === 'login' ? 'auth.register-link-form' : 'auth.login-link-form')}
          </a>
        </div>
      </form>
    </div>
  );

  return (
    <WindowFrame name="auth" header={t(form === 'login' ? 'auth.login-header' : 'auth.register-header')} closing={instance.closing} mode={form}>
      <div className="tab_contents">
        <div className="auth_cont lbx_cont">
          {renderForm('register')}
          {renderForm('login')}
        </div>
      </div>
    </WindowFrame>
  );
}
